import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Button, Typography, Divider, Row, Col, Table } from "antd";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import AssetForm from "./AssetForm";
import EmptyForm from "./EmptyForm";
import ErrorBoundary from "./ErrorBoundary";
import StructureForm from "./StructureForm";
import InfrastructureForm from "./InfrastructureForm";
import StaffForm from "./StaffForm";
import MachineryForm from "./MachineryForm";
import LandForm from "./LandForm";
import MaterialForm from "./MaterialForm";
import ProvisioForm from "./ProvisionForm";
import LivestockForm from "./LivestockForm";
import CropForm from "./CropForm";
import StoredForm from "./StoredForm";
//TLC
import TlcComponentForm from "../Telecommunication/TlcComponentForm";
import TlcServiceForm from "../Telecommunication/TlcServiceForm";
//ELECTRICAL
import ElectricalComponentForm from "../Electrical/ElectricalComponentForm";
//TRANSPORT
import TransportComponentForm from "../Transport/TransportComponentForm";
//BUILDING (non-ordinary)
import AddedBlockForm from "../Building/AddedBlockForm";
//BUILDING (CH content — both ordinary and non-ordinary)
import ArtisticGoodForm from "../Building/ArtisticGoodForm";

import axios from "axios";
import "./Enterprise.css";

const { Title } = Typography;

const AssetInfo = (props) => {
  const [assetTypes, setAssetTypes] = useState([]);
  const [dataState, setDataState] = useState({ data: [] });
  const [listState, setListState] = useState({
    pagination: { pageSize: 10, current: 1 },
    filters: {},
    sorter: {}
  });
  const [loaderState, setLoaderState] = useState(false);
  const [totalPageState, setTotalPageState] = useState(0);
  const [editState, setEditState] = useState({ asset: {}, valid: false });
  const [detailsState, setDetailsState] = useState({ asset: {}, valid: false });
  const [isAssetEditMode, setIsAssetEditMode] = useState(false);
  const [selectedRowKey, setSelectedRowKey] = useState(null);

  // Use the parent isEditMode
  const isEditMode = props.isEditMode || false;

  const reloadAssets = (maintainSelection = true) => {
    // Clear data if no farm is selected or if farm ID is missing
    if (!props.record.enterprise || !props.record.enterprise.id) {
      setDataState({ data: [] });
      setTotalPageState(0);
      setEditState({ asset: {}, valid: false });
      setDetailsState({ asset: {}, valid: false });
      setSelectedRowKey(null);
      return;
    }
    // Remember the currently selected asset ID before reloading only if maintainSelection is true
    const currentSelectedId = maintainSelection ? selectedRowKey : null;

    setLoaderState(true);
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/" + props.type.apiPath + "/components/", {
        params: {
          parentId: props.record.enterprise.id,
          pageSize: listState.pagination.pageSize,
          pageNumber: listState.pagination.current,
          sortField: listState.sorter.field,
          sortOrder: listState.sorter.order,
          ...listState.filters
        }
      })
      .then(({ data }) => {
        console.log("Assets loaded from server:", data.data);

        // Process the data to ensure all required fields are present
        const processedData = data.data.map(asset => {
          // Process typePath for Cascader compatibility
          if (asset.typePath) {
            // Store the string array representation needed by Cascader
            asset.typeArray = asset.typePath.split('/');
          }

          return asset;
        });

        setDataState({ data: processedData });
        setTotalPageState(data.totalCount);

        // If there was a selected asset, find it in the new data and update editState
        if (currentSelectedId !== null && currentSelectedId !== -1) {
          const selectedAsset = processedData.find(asset => asset.id === currentSelectedId);
          if (selectedAsset) {
            console.log("Re-selecting asset:", selectedAsset);
            setEditState({
              asset: selectedAsset,
              valid: true
            });
            setSelectedRowKey(currentSelectedId);
          }
        } else if (!maintainSelection && processedData.length > 0) {
          // When switching buildings and there are assets, select the first one
          const firstAsset = processedData[0];
          console.log("Auto-selecting first asset:", firstAsset);
          setEditState({
            asset: firstAsset,
            valid: true
          });
          setDetailsState({
            asset: firstAsset,
            valid: true
          });
          setSelectedRowKey(firstAsset.id);
        }
      })
      .catch(function (error) {
        console.log("Error loading assets:", error);
      })
      .finally(function () {
        setLoaderState(false);
      });
  };

  const saveAsset = () => {
    if (!isAssetEditMode || !editState.valid || !detailsState.valid) {
      return;
    }

    props.startLoading();

    // Determine whether we're creating a new asset or updating an existing one
    const isNewAsset = editState.asset.id === -1;

    // Create a clean copy of the asset data with all required fields explicitly set
    const assetData = {
      name: editState.asset.name || "",
      geometry: editState.asset.geometry || "",
      typePath: editState.asset.typePath || "",
      componentKind: editState.asset.componentKind || null,
      typeName: editState.asset.typeName || "",
      typeDescriptionPath: editState.asset.typeDescriptionPath || "",
      insured: editState.asset.insured || false,
      // Include any other fields from detailsState
      ...detailsState.asset,
      // After the spread so a stale id/parentId in detailsState can't override them.
      parentId: props.record.enterprise.id,
      id: isNewAsset ? null : editState.asset.id,
    };
    // Some Component subclasses have their own dedicated save endpoint
    // (rather than the generic per-enterprise /components/save). When the
    // asset's typeName matches one of those, route to the specialised URL.
    const SPECIALISED_SAVE_ENDPOINTS = {
      'added_block':   '/added-block/save',
      'artistic_good': '/artistic-good/save',
    };
    const saveUrl = SPECIALISED_SAVE_ENDPOINTS[assetData.typeName]
      ? process.env.REACT_APP_API_ENDPOINT + SPECIALISED_SAVE_ENDPOINTS[assetData.typeName]
      : process.env.REACT_APP_API_ENDPOINT + "/" + props.type.apiPath + "/components/save";

    axios
      .post(saveUrl, assetData)
      .then(({ data }) => {
        setSelectedRowKey(data.id);
        setIsAssetEditMode(false);
        reloadAssets();
        setTimeout(() => { props.stopLoading("success"); }, 1000);
      })
      .catch(function (error) {
        console.log("Error saving asset:", error);
        setTimeout(() => { props.stopLoading("failure"); }, 1000);
      });
  };

  // Update this function in AssetInfo.js to properly handle the typeName for read-only components
  const showAsset = id => {
    const toShow = dataState.data.find(i => i.id === id);
    if (toShow) {
      // Make a deep copy to prevent reference issues
      const toShowCopy = JSON.parse(JSON.stringify(toShow));

      // Ensure we have typeArray for Cascader from typePath IF the component is not read-only
      // Read-only components will just use the typeDescriptionPath directly
      if (!toShowCopy.readonly && toShowCopy.typePath && !toShowCopy.typeArray) {
        toShowCopy.typeArray = toShowCopy.typePath.split('/');
      }

      // IMPORTANT: Extract componentKind from typePath if it's not set
      if (toShowCopy.typePath && (!toShowCopy.componentKind || toShowCopy.componentKind === undefined)) {
        const typePathArray = toShowCopy.typePath.split('/');
        toShowCopy.componentKind = typePathArray[typePathArray.length - 1];
      }

      // Make sure read-only components have a properly formatted type description
      if (toShowCopy.readonly && (!toShowCopy.typeDescriptionPath || toShowCopy.typeDescriptionPath.length === 0)) {
        // If typeDescriptionPath is missing, use the typeName instead
        if (toShowCopy.typeName) {
          // Convert snake_case to Title Case (e.g., "building_component" -> "Building Component")
          toShowCopy.typeDescriptionPath = toShowCopy.typeName
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        }
      }


      setEditState({
        asset: toShowCopy,
        valid: true
      });
      setDetailsState({
        asset: {},
        valid: true
      });
      setIsAssetEditMode(false);
      setSelectedRowKey(id);
    } else {
      console.warn("Asset with ID", id, "not found");
    }
  };

  const editAsset = id => {
    if (!isEditMode) return; // Don't allow editing assets when farm isn't in edit mode

    const toEdit = dataState.data.find(i => i.id === id);
    if (toEdit) {
      // Create deep copies to avoid reference issues
      const toEditCopy = JSON.parse(JSON.stringify(toEdit));

      // Ensure we have typeArray for Cascader from typePath
      if (toEditCopy.typePath && !toEditCopy.typeArray) {
        toEditCopy.typeArray = toEditCopy.typePath.split('/');
      }

      // IMPORTANT: Extract componentKind from typePath if it's not set
      if (toEditCopy.typePath && (!toEditCopy.componentKind || toEditCopy.componentKind === undefined)) {
        const typePathArray = toEditCopy.typePath.split('/');
        toEditCopy.componentKind = typePathArray[typePathArray.length - 1];
      }

      setEditState({ asset: toEditCopy, valid: true });

      // Preserve the asset data in detailsState as well
      setDetailsState({
        asset: toEditCopy, // Use same object instead of empty object
        valid: true
      });

      setIsAssetEditMode(true);
      setSelectedRowKey(id);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setListState({ pagination: pagination, filters: filters, sorter: sorter });
  };

  const newAsset = () => {
    if (!isEditMode) return; // Don't allow adding assets when farm isn't in edit mode

    // Create a new asset with ALL required fields initialized
    const newAsset = {
      id: -1,
      name: '',
      geometry: '',
      typePath: '',
      componentKind: null,
      insured: false
    };

    setDataState({
      data: [
        newAsset,
        ...(dataState.data)
      ]
    });

    setEditState({
      asset: newAsset,
      valid: false
    });

    setDetailsState({
      asset: {},
      valid: true  // Default to true for new assets since detail forms have no required fields
    });

    setIsAssetEditMode(true);
    setSelectedRowKey(-1);
  };

  const cancelEdit = () => {
    // If we're canceling a new asset, remove it from the temporary display
    if (editState.asset && editState.asset.id === -1) {
      setDataState({
        data: dataState.data.filter(asset => asset.id !== -1)
      });
      setSelectedRowKey(null);
      setEditState({ asset: {}, valid: false });
      setDetailsState({ asset: {}, valid: false });
    } else {
      // If we're canceling an edit to an existing asset, remember its ID
      const assetId = editState.asset.id;
      // We'll reselect this asset after reloading
      setSelectedRowKey(assetId);
    }

    setIsAssetEditMode(false);
    reloadAssets();
  };

  const deleteAsset = id => {
    if (!isEditMode) return; // Don't allow deleting assets when farm isn't in edit mode

    const toDelete = dataState.data.find(i => i.id === id);
    if (!toDelete) return;

    props.startLoading();
    axios
      .delete(process.env.REACT_APP_API_ENDPOINT + "/" + props.type.apiPath + "/components/delete", {
        params: {
          id: toDelete.id
        }
      })
      .then(({ data }) => {
        reloadAssets();
        setEditState({ asset: {}, valid: false });
        setDetailsState({ asset: {}, valid: false });
        setSelectedRowKey(null);
        setTimeout(() => { props.stopLoading("success"); }, 1000);
      })
      .catch(function (error) {
        console.log(error);
        setTimeout(() => { props.stopLoading("failure"); }, 1000);
      });
  };

  const assetChange = (newRecord) => {
    var newState = { ...editState, ...newRecord };
    setEditState(newState);
  };

  const assetDetailsChange = (newRecord) => {
    var newState = {
      ...detailsState,
      asset: {
        ...detailsState.asset,
        ...newRecord.asset
      },
      valid: newRecord.valid
    };
    setDetailsState(newState);
  };

  const components = {
    //FARM
    'empty': EmptyForm,
    'structure': StructureForm,
    'infrastructure': InfrastructureForm,
    'machinery': MachineryForm,
    'staff': StaffForm,
    'land': LandForm,
    'material': MaterialForm,
    'provision': ProvisioForm,
    'livestock': LivestockForm,
    'crop': CropForm,
    'stored': StoredForm,
    //TLC
    'tlc_service': TlcServiceForm,
    'tlc_component': TlcComponentForm,
    //ELETRICAL
    'electrical_component': ElectricalComponentForm,
    //ROAD
    'transport_component': TransportComponentForm,
    //BUILDING
    'added_block': AddedBlockForm,
    'artistic_good': ArtisticGoodForm,
  };

  const getComponents = () => {
    // Use typeName directly from the asset data instead of searching by ID
    const componentName = editState.asset ? editState.asset.typeName : null;
    const DetailsComponent = components[componentName] || EmptyForm;

    console.log("Getting component for typeName:", componentName);

    return <DetailsComponent
      key={`component-${componentName}-${editState.asset ? editState.asset.id : 'new'}`}
      record={editState}
      parentCallback={assetDetailsChange}
      isEditMode={isAssetEditMode}
    />;
  };

  // Load asset types
  useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + "/enums/component-kinds/" + props.type.value.toLowerCase())
      .then(({ data }) => {
        setAssetTypes(data);
      })
      .catch(function (error) {
        console.log(error);
      });
  }, [props.type.name]);


  // Track previous farm ID to detect changes
  const [prevFarmId, setPrevFarmId] = useState(null);

  // Load assets when listState changes or farm ID changes
  useEffect(() => {
    if (props.record.enterprise && props.record.enterprise.id) {
      const farmIdChanged = prevFarmId !== props.record.enterprise.id;
      
      if (farmIdChanged) {
        // Clear selection when switching to a different enterprise
        setEditState({ asset: {}, valid: false });
        setDetailsState({ asset: {}, valid: false });
        setSelectedRowKey(null);
        setIsAssetEditMode(false);
        setPrevFarmId(props.record.enterprise.id);
      }
      
      // Don't maintain selection if farm ID changed
      reloadAssets(!farmIdChanged);
    }
  }, [listState, props.record.enterprise, props.record.enterprise.id, props.type.name, prevFarmId]);

  useEffect(() => {
    if (editState.asset && editState.asset.componentKind) {
      // This will force re-evaluation of the component when componentKind changes
      console.log("Asset component kind changed to:", editState.asset.componentKind);

      // If we want to load detailed asset data, we could do that here
      if (selectedRowKey && selectedRowKey !== -1) {
        // Fetch detailed data for this asset type if needed
        console.log("Loading detailed data for asset:", selectedRowKey);
      }
    }
  }, [editState.asset.componentKind, selectedRowKey, editState.asset]);

  useEffect(() => {
    // Clear asset data when enterprise type changes
    setDataState({ data: [] });
    setTotalPageState(0);
    setEditState({ asset: {}, valid: false });
    setDetailsState({ asset: {}, valid: false });
    setSelectedRowKey(null);
  }, [props.type.name]);

  const columns = [
    {
      title: "Name",
      dataIndex: "name"
    },
    {
      title: "Type",
      dataIndex: "typeDescriptionPath",
      // typeDescriptionPath may be a single segment ("added_block") or a
      // slash-joined hierarchy ("structure/aviation_hangar"). Translate
      // each segment independently; unknown segments pass through.
      render: (text) => text
        ? text.split('/').map(seg => translateTypeName(seg.trim())).join(' / ')
        : text,
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => {
        if (!record.readonly && isEditMode && !isAssetEditMode) {
          // Check if this is a building_component type that should not be deletable
          const isDeletable = record.typeName !== 'building_component';
          
          return (
            <span>
              <Button
                type="default"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  editAsset(record.id);
                }}
              >
                Edit
              </Button>
              {isDeletable && (
                <>
                  <Divider type="vertical" />
                  <Button
                    type="default"
                    size="small"
                    onClick={() => deleteAsset(record.id)}
                  >
                    Delete
                  </Button>
                </>
              )}
            </span>
          );
        } else {
          return null;
        }
      }
    }
  ];

  const { t } = useTranslation();

  // Translate a raw Type `name` (e.g. "added_block") to a friendly label
  // (e.g. "Added block") via translation.json. Falls back to the original
  // string when no key is registered, so it's safe to apply to every Type
  // including the legacy ones that already have descriptions baked into
  // the DB (those reach us as `label="Machinery"` already and have no
  // `componentType.Machinery` key, so they pass through unchanged).
  const translateTypeName = (raw) => {
    if (!raw) return raw;
    const key = `componentType.${raw}`;
    return i18n.exists(key) ? t(key) : raw;
  };

  // Walk the Cascader tree and replace each entry's `label` with the
  // translated value when one exists.
  const translateAssetTypes = (types) => types.map(node => {
    const translated = { ...node, label: translateTypeName(node.name || node.label) };
    if (Array.isArray(node.children) && node.children.length > 0) {
      translated.children = translateAssetTypes(node.children);
    }
    return translated;
  });

  // Hide `artistic_good` from the asset-type Cascader unless the parent
  // BuiltEnvironment has `culturalHeritage=true` — the server rejects the
  // save otherwise, so don't tempt the surveyor with an unsavable choice.
  // (`added_block` is gated server-side: the type catalog only seeds it
  // under NON_ORDINARY_BUILDING, so no FE filter needed there.)
  const isCulturalHeritage = props.record.enterprise && props.record.enterprise.culturalHeritage === true;
  const filteredByCH = isCulturalHeritage
    ? assetTypes
    : assetTypes.filter(t => t.name !== 'artistic_good');
  const visibleAssetTypes = translateAssetTypes(filteredByCH);

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4}>
            {props.record.enterprise.vat ? 'Assets & production for ' + props.record.enterprise.vat : 'Assets & production'}
          </Title>
        </Col>
        <Col>
          <Button
            disabled={!isEditMode || isAssetEditMode}
            onClick={() => reloadAssets()}
          >
            Refresh
          </Button>
          <Button
            style={{ marginLeft: 8 }}
            disabled={!isEditMode || isAssetEditMode}
            onClick={() => newAsset()}
          >
            New
          </Button>
          <Button
            style={{ marginLeft: 8 }}
            type="primary"
            disabled={!isAssetEditMode || (!editState.valid || !detailsState.valid)}
            onClick={() => saveAsset()}
          >
            Save
          </Button>
          <Button
            style={{ marginLeft: 8 }}
            disabled={!isAssetEditMode}
            onClick={() => cancelEdit()}
          >
            Cancel
          </Button>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Table
            size="small"
            scroll={{ y: 150 }}
            columns={columns}
            rowKey={record => record.id}
            dataSource={dataState.data}
            pagination={{ ...listState.pagination, total: totalPageState }}
            loading={loaderState}
            onChange={handleTableChange}
            onRow={(record) => ({
              onClick: () => {
                if (!isAssetEditMode) {
                  showAsset(record.id);
                }
              },
              className: record.id === selectedRowKey ? 'selected-row' : '',
              style: isAssetEditMode ? { cursor: 'not-allowed' } : {}
            })}
            rowClassName={(asset) => asset.id === selectedRowKey ? 'selected-row' : ''}
          />
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <AssetForm
            record={editState}
            parentCallback={assetChange}
            assetTypes={visibleAssetTypes}
            isEditMode={isAssetEditMode}
          />
        </Col>
        <Col span={12}>
          <ErrorBoundary>
            {getComponents()}
          </ErrorBoundary>
        </Col>
      </Row>
    </div>
  );
};

const mapDispatchToProps = dispatch => {
  return {
    startLoading: () => dispatch({ type: "START_LOADING" }),
    stopLoading: val => dispatch({ type: "STOP_LOADING", status: val })
  };
};

export default connect(null, mapDispatchToProps)(AssetInfo);